/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Shared builders for the article SEO surface that gets baked into HTML —
 * plain ESM JavaScript on purpose (the deepMerge.js pattern) because it has
 * consumers that cannot share TypeScript:
 *   1. src/lib/seoHead.js injectHead — the head of every page, which the bake
 *      (scripts/prerender.mjs, plain Node) and the request-time head
 *      (server/seoMeta.ts, via tsconfig.server.json's allowJs) both call
 *   2. the client pages, after hydration
 *   3. unit tests
 *
 * Every builder MIRRORS a client-side implementation; the client remains the
 * source of truth and replaces the baked JSON-LD post-hydration via
 * setJsonLd(id) in src/hooks/useSEO.ts (found-by-id, replaced in place — so
 * ids here MUST match the client-managed ids: jsonld-article,
 * jsonld-breadcrumb, jsonld-faq, jsonld-article-custom). Keep in sync with:
 *   - src/pages/ArticlePage.tsx (Article payload + Person author node)
 *   - src/hooks/useSEO.ts buildBreadcrumbJsonLd (Home prepend, empty filter)
 *
 * `inLanguage` is the language of the WORDS on the page
 * ({@link articleContentLanguage}), not the language of the URL, so an
 * untranslated article's /en/ page never declares Dutch text as English. The
 * client reaches the same answer by a different route — ArticlePage.tsx derives
 * it from the `hasEnglish` field on the article payload, which the server
 * computes with the same predicate — so the baked node and the one it replaces
 * after hydration agree. Change one, change both.
 */

import { effectiveArticleUpdatedAt } from './articleDates.js';
import { hasEnglishContent } from './articleEnglish.js';
import { estimateReadingMinutes, readingTimeISO } from './readingTimeCore.js';
import { BREADCRUMB_JSONLD_ID, buildBreadcrumbJsonLd } from './breadcrumbSchema.js';
import { clampMetaDescription } from './templateVars.js';
import { escapeHtml } from './htmlEscape.js';
import { CSP_NONCE_ATTR } from './cspNonce.js';

export const SEO_HOST = 'https://ere-vergelijk.nl';

/**
 * Resolve the translation actually served for an article in a language.
 * Mirrors server/routes/content.ts: a stub translation (empty title) falls
 * back to NL so we never emit empty headline/description.
 * @param {any} article
 * @param {'nl'|'en'} lang
 * @returns {{title?: string, excerpt?: string, blocks?: any[]}}
 */
export function effectiveArticleTranslation(article, lang) {
  const requested = article?.[lang];
  const hasContent = requested && typeof requested.title === 'string' && requested.title.trim();
  return hasContent ? requested : (article?.nl ?? {});
}

/** @type {Readonly<Record<string, string>>} */
const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'", nbsp: ' ' };

/**
 * The words of a block's HTML: tags dropped, the common entities decoded.
 *
 * @param {unknown} html
 * @returns {string}
 */
const plainText = (html) =>
  typeof html === 'string'
    ? html
        .replace(/<[^>]*>/g, ' ')
        .replace(/&(amp|lt|gt|quot|#39|apos|nbsp);/g, (_, name) => ENTITIES[name])
        .replace(/\s+/g, ' ')
        .trim()
    : '';

/**
 * An article's meta description — ONE chain for the head (src/lib/seoHead.js)
 * and ArticlePage, so the baked head, the request-time head and the hydrated
 * tab say the same thing:
 *   1. the SEO tab's `article_<id>_description`, exactly as the editor typed it
 *   2. the excerpt, capped at META_DESCRIPTION_MAX (excerpts run to 500+)
 *   3. the first block that has text, as plain words and capped
 *   4. `fallback` (the head passes the knowledge base's description)
 *
 * Until 2026-10-04 there was no step 3, and the client had no step 4: an
 * article without an excerpt in one language baked the knowledge base's own
 * description (one sentence on two URLs) and hydrated to an empty one.
 *
 * @param {{ override?: unknown, excerpt?: unknown, blocks?: any[] | null, fallback?: string }} input  `blocks`: the article's content blocks — a FAQ block has no `text` and is skipped.
 * @returns {string}
 */
export function articleMetaDescription({ override, excerpt, blocks, fallback = '' }) {
  const own = typeof override === 'string' ? override.trim() : '';
  if (own) return own;
  const fromExcerpt = clampMetaDescription(plainText(excerpt));
  if (fromExcerpt) return fromExcerpt;
  for (const block of Array.isArray(blocks) ? blocks : []) {
    const words = clampMetaDescription(plainText(block?.text));
    if (words) return words;
  }
  return fallback;
}

/**
 * The language of the WORDS actually served at this URL — which is not always
 * the language of the URL. `/en/kennisbank/<slug>` of an untranslated article
 * serves the Dutch fallback (see {@link effectiveArticleTranslation}), and
 * every language declaration derived from that page must say so: `inLanguage`
 * on the Article/FAQ JSON-LD and `og:locale` in the head (src/lib/seoHead.js).
 *
 * Declaring `en-US` there is not a cosmetic slip — it is the machine-readable
 * claim "this text is English", made about Dutch text, to exactly the readers
 * (crawlers, AI answer engines, translation pipelines) who cannot look and
 * judge for themselves. The `<html lang>` attribute is deliberately NOT derived
 * from this: it belongs to the rendered UI chrome, which really is English on
 * an /en/ URL, and the client language context owns it.
 *
 * @param {any} article — a raw CMS article record (needs the `en` slot)
 * @param {'nl'|'en'} lang — the language of the URL
 * @returns {'nl'|'en'}
 */
export function articleContentLanguage(article, lang) {
  return lang === 'en' && !hasEnglishContent(article) ? 'nl' : lang;
}

/**
 * Absolute URL for a possibly site-relative asset path.
 * @param {string} pathOrUrl
 * @param {string} [host]
 * @returns {string}
 */
export function absoluteSeoUrl(pathOrUrl, host = SEO_HOST) {
  if (!pathOrUrl) return '';
  if (pathOrUrl.startsWith('http')) return pathOrUrl;
  return `${host}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

/**
 * Person author node for Article JSON-LD; Organization fallback when the
 * article has no resolvable author. Mirrors ArticlePage.tsx.
 * @param {null | {displayName?: string, title?: string, avatarPath?: string,
 *   linkedinUrl?: string, twitterUrl?: string, websiteUrl?: string}} author
 * @param {{canonicalUrl: string, host?: string}} opts
 * @returns {object}
 */
export function buildArticleAuthorNode(author, { canonicalUrl, host = SEO_HOST }) {
  if (!author || !author.displayName) {
    return { '@type': 'Organization', name: 'ERE-Vergelijk', url: host };
  }
  const sameAs = [author.linkedinUrl, author.twitterUrl, author.websiteUrl].filter(Boolean);
  const authorImage = author.avatarPath ? absoluteSeoUrl(author.avatarPath, host) : undefined;
  return {
    '@type': 'Person',
    name: author.displayName,
    ...(author.title ? { jobTitle: author.title } : {}),
    ...(authorImage ? { image: authorImage } : {}),
    ...(sameAs.length > 0 ? { sameAs } : {}),
    url: canonicalUrl,
  };
}

/**
 * Article JSON-LD payload. Mirrors ArticlePage.tsx (incl. timeRequired via
 * the shared reading-time core, so baked and hydrated payloads match).
 * @param {{article: any, lang: 'nl'|'en', headline: string, description: string,
 *   imageUrl: string, canonicalUrl: string, blocks?: any[],
 *   author?: object|null, host?: string}} opts
 * @returns {object}
 */
export function buildArticleJsonLd(opts) {
  const { article, lang, headline, description, imageUrl, canonicalUrl, author = null } = opts;
  const host = opts.host ?? SEO_HOST;
  const blocks = opts.blocks ?? effectiveArticleTranslation(article, lang).blocks ?? [];
  // `headline`/`description`/`blocks` are whatever effectiveArticleTranslation
  // resolved, so inLanguage must describe THAT, not the URL — see
  // articleContentLanguage. (timeRequired below keeps the URL language: it
  // mirrors the client's own estimate, and a baked/hydrated split there would
  // show up as two different reading times on one page.)
  const contentLang = articleContentLanguage(article, lang);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    image: imageUrl,
    inLanguage: contentLang === 'nl' ? 'nl-NL' : 'en-US',
    datePublished: article.createdAt,
    dateModified: effectiveArticleUpdatedAt(article),
    timeRequired: readingTimeISO(estimateReadingMinutes(blocks, lang)),
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    author: buildArticleAuthorNode(author, { canonicalUrl, host }),
    publisher: {
      '@type': 'Organization',
      name: 'ERE-Vergelijk',
      logo: { '@type': 'ImageObject', url: `${host}/logo.png` },
    },
  };
}

/**
 * Question text as plain words: tags dropped, the common entities decoded.
 *
 * @param {unknown} s
 * @returns {string}
 */
const stripFaqHtml = (s) =>
  String(s ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * FAQPage JSON-LD from every `kind: 'faq'` block — the ONE builder: the baked
 * head (buildArticleHeadExtras below, the begrip pages in routeHeadSchema.js),
 * ArticlePage and BegripPage after hydration and the admin's FAQ block preview
 * (the last two through src/lib/articleFAQSchema.ts, its typed face; until
 * 2026-10-14 that file held a second copy). Per-block includeInSchema and
 * per-item excludeFromSchema opt-outs, question HTML stripped, answer HTML
 * kept, and `inLanguage` the language of the words (the caller passes it).
 * Returns null when no qualifying question survives.
 * @param {any[]|undefined} blocks
 * @param {'nl'|'en'} lang
 * @returns {object|null}
 */
export function buildArticleFAQJsonLd(blocks, lang) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  const questions = [];
  for (const block of blocks) {
    if (block?.kind !== 'faq') continue;
    if (block.includeInSchema === false) continue;
    for (const item of block.items ?? []) {
      if (item.excludeFromSchema) continue;
      const q = stripFaqHtml(item.q);
      const a = (item.a || '').trim();
      if (!q || !a) continue;
      questions.push({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      });
    }
  }
  if (questions.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: lang === 'nl' ? 'nl-NL' : 'en-US',
    mainEntity: questions,
  };
}

/**
 * BreadcrumbList JSON-LD. Re-exported from src/lib/breadcrumbSchema.js, which
 * is now the single implementation shared with src/hooks/useSEO.ts — this
 * module kept a hand-mirrored copy until 2026-09-01. Existing importers (and
 * src/lib/__tests__/articleSeo.test.ts) keep working unchanged.
 */
export { buildBreadcrumbJsonLd };

/**
 * The body of a JSON-LD <script> written into served or baked HTML: the JSON
 * text with every `<` written as the JSON escape `\u003c`, which parses back
 * to the same value. Escaping only `</script` was not enough (2026-10-03
 * review): the HTML parser reads a script's body as raw text, and a `<!--`
 * followed by `<script` inside it — an admin's brand or model name, an SEO
 * tab's custom JSON-LD — switches it to a state in which `</script>` no
 * longer ends the element, so the browser swallowed the elements after it.
 * With no `<` left in the body there is nothing for it to read. Valid JSON
 * holds a `<` only inside a string, never right after an escaping backslash,
 * so the replacement is safe on any valid JSON text. Every ld+json writer of
 * the server head and the bake goes through this (pinned in
 * server/__tests__/routeHeadSchema.test.ts); the pages set `textContent` and
 * need nothing.
 * @param {string} json  serialized JSON
 * @returns {string}
 */
export function jsonLdScriptBody(json) {
  return String(json).replace(/</g, '\\u003c');
}

/**
 * Serialize a JSON-LD payload into a <script> tag with a client-managed id
 * (setJsonLd replaces it in place post-hydration — never duplicates).
 * ld+json is data, not executable script, so it needs no CSP nonce; it still
 * carries the nonce placeholder (src/lib/cspNonce.js), which a response
 * swaps for its own nonce, so that every script we write follows one rule.
 * The body goes through {@link jsonLdScriptBody} so payload content can't
 * break out of the tag.
 * @param {string} id
 * @param {object} payload
 * @returns {string}
 */
export function jsonLdScriptTag(id, payload) {
  return `  <script type="application/ld+json" id="${id}"${CSP_NONCE_ATTR}>${jsonLdScriptBody(JSON.stringify(payload))}</script>`;
}

/**
 * The full set of extra <head> lines for a baked article page: article:* meta
 * plus the three auto JSON-LD scripts and the optional SEO-Studio custom one.
 * ONE assembly point, called by injectHead in src/lib/seoHead.js for the bake
 * and the request-time head alike.
 * @param {{article: any, lang: 'nl'|'en', headline: string, description: string,
 *   imageUrl: string, canonicalUrl: string, blocks?: any[], author?: object|null,
 *   kennisbankLabel: string, customJsonLd?: string|null, host?: string}} opts
 * @returns {string[]}
 */
export function buildArticleHeadExtras(opts) {
  const host = opts.host ?? SEO_HOST;
  const { article, lang, headline, description, imageUrl, canonicalUrl } = opts;
  const blocks = opts.blocks ?? effectiveArticleTranslation(article, lang).blocks ?? [];
  const canonicalPath = canonicalUrl.startsWith(host)
    ? canonicalUrl.slice(host.length)
    : canonicalUrl;

  const lines = [
    `  <meta property="article:published_time" content="${escapeHtml(article.createdAt)}" />`,
    `  <meta property="article:modified_time" content="${escapeHtml(effectiveArticleUpdatedAt(article))}" />`,
    jsonLdScriptTag(
      'jsonld-article',
      buildArticleJsonLd({
        article,
        lang,
        headline,
        description,
        imageUrl,
        canonicalUrl,
        blocks,
        author: opts.author,
        host,
      })
    ),
    jsonLdScriptTag(
      BREADCRUMB_JSONLD_ID,
      buildBreadcrumbJsonLd(
        [
          { label: opts.kennisbankLabel, path: `/${lang}/kennisbank` },
          { label: headline, path: canonicalPath },
        ],
        lang,
        host
      )
    ),
  ];
  // The FAQ items come out of the same resolved blocks as the Article node, so
  // they carry the same content language — a stub /en/ page's questions and
  // answers are Dutch.
  const faq = buildArticleFAQJsonLd(blocks, articleContentLanguage(article, lang));
  if (faq) lines.push(jsonLdScriptTag('jsonld-faq', faq));
  if (typeof opts.customJsonLd === 'string' && opts.customJsonLd.trim()) {
    lines.push(
      `  <script type="application/ld+json" id="jsonld-article-custom" data-source="seo-studio"${CSP_NONCE_ATTR}>${jsonLdScriptBody(opts.customJsonLd)}</script>`
    );
  }
  return lines;
}
